import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Select, Row, Col, Typography, Button, Radio, Space, Divider, Layout, Flex } from 'antd';
import { ArrowLeftOutlined, QrcodeOutlined, WalletOutlined, CreditCardOutlined, TrophyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import './DepositPage.css';
const { Title, Text } = Typography;
const { Option } = Select;
const { Content } = Layout;

interface DepositPackage {
  id: string;
  atPoints: number;
  bonus: number;
  price: number;
  icon: string;
  popular?: boolean;
}

interface PaymentMethod {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
}

const DepositPage: React.FC = () => {
  const { t } = useTranslation();
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const [selectedServer, setSelectedServer] = useState<string>();
  const [selectedCharacter, setSelectedCharacter] = useState<string>();
  const [selectedPackage, setSelectedPackage] = useState<string>();
  const [selectedPayment, setSelectedPayment] = useState<string>();

  // Get game name based on gameId
  const getGameName = (id: string | undefined) => {
    // TODO: Fetch from API or game list
    const gameMap: { [key: string]: string } = {
      '1': t('gameList.game1Name'),
      // Add more games as needed
    };
    return gameMap[id || ''] || t('gameList.updating');
  };

  // Mock data - replace with API calls
  const servers = [
    { id: '1', name: 'Máy chủ 1 - Hoàng Kim' },
    { id: '2', name: 'Máy chủ 2 - Bạch Kim' },
    { id: '3', name: 'Máy chủ 3 - Kim Cương' },
  ];

  const characters = [
    { id: '1', name: 'Chiến Binh Rồng', level: 99 },
    { id: '2', name: 'Pháp Sư Băng', level: 85 },
    { id: '3', name: 'Sát Thủ Bóng Đêm', level: 92 },
  ];

  const depositPackages: DepositPackage[] = [
    { id: '1', atPoints: 100, bonus: 0, price: 20000, icon: '💎' },
    { id: '2', atPoints: 500, bonus: 50, price: 100000, icon: '💎', popular: true },
    { id: '3', atPoints: 1000, bonus: 150, price: 200000, icon: '💎' },
    { id: '4', atPoints: 2000, bonus: 400, price: 400000, icon: '💎' },
    { id: '5', atPoints: 5000, bonus: 1500, price: 1000000, icon: '💎', popular: true },
    { id: '6', atPoints: 10000, bonus: 3500, price: 2000000, icon: '💎' },
  ];

  const paymentMethods: PaymentMethod[] = [
    {
      id: 'qrcode',
      name: t('deposit.qrCode'),
      icon: <QrcodeOutlined />,
      description: t('deposit.qrCodeDesc')
    },
    {
      id: 'gpay',
      name: t('deposit.gpay'),
      icon: <WalletOutlined />,
      description: t('deposit.gpayDesc')
    },
    {
      id: 'apotapay',
      name: t('deposit.apotapay'),
      icon: <CreditCardOutlined />,
      description: t('deposit.apotapayDesc')
    }
  ];

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(price);
  };

  const handleDeposit = () => {
    if (!selectedServer || !selectedCharacter || !selectedPackage || !selectedPayment) {
      // Show error message
      return;
    }
    // TODO: Implement deposit logic
    console.log({
      gameId,
      server: selectedServer,
      character: selectedCharacter,
      package: selectedPackage,
      payment: selectedPayment
    });
  };

  return (
    <Layout className="deposit-page-container">
      <Content className="deposit-content">
        <Title level={2} className="page-title">
          {t('deposit.pageTitle', { gameName: getGameName(gameId) })}
        </Title>
        {/* Section 1: Character Information */}
          <Card title={t('deposit.characterInfo')} className="section-card" variant="borderless">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Text strong>{t('deposit.server')}</Text>
                <Select
                  placeholder={t('deposit.selectServer')}
                  className="select-input"
                  size="large"
                  value={selectedServer}
                  onChange={setSelectedServer}
                >
                  {servers.map(server => (
                    <Option key={server.id} value={server.id}>
                      {server.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} md={12}>
                <Text strong>{t('deposit.character')}</Text>
                <Select
                  placeholder={t('deposit.selectCharacter')}
                  className="select-input"
                  size="large"
                  value={selectedCharacter}
                  onChange={setSelectedCharacter}
                  disabled={!selectedServer}
                >
                  {characters.map(char => (
                    <Option key={char.id} value={char.id}>
                      {char.name} ({t('deposit.level')} {char.level})
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </Card>

          {/* Section 2: Deposit Packages */}
          <Card title={t('deposit.depositPackage')} className="section-card" variant="borderless">
            <Row gutter={[16, 16]}>
              {depositPackages.map(pkg => (
                <Col xs={24} sm={12} md={8} key={pkg.id}>
                  <Card
                    hoverable
                    className={`package-card ${selectedPackage === pkg.id ? 'selected' : ''} ${pkg.popular ? 'popular' : ''}`}
                    onClick={() => setSelectedPackage(pkg.id)}
                  >
                    {pkg.popular && (
                      <Space className="popular-badge">
                        <TrophyOutlined /> {t('deposit.popular')}
                      </Space>
                    )}
                    <Space direction="vertical" align="center" className="package-content">
                      <span className="package-icon">{pkg.icon}</span>
                      <Title level={3} className="package-points">
                        {pkg.atPoints.toLocaleString()} {t('deposit.atPoint')}
                      </Title>
                      {pkg.bonus > 0 && (
                        <Text type="success" className="package-bonus">
                          {t('deposit.bonusPoint', { bonus: pkg.bonus })}
                        </Text>
                      )}
                      <Divider className="package-divider" />
                      <Title level={4} className="package-price">
                        {formatPrice(pkg.price)}
                      </Title>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>

          {/* Section 3: Payment Methods */}
          <Card title={t('deposit.paymentMethod')} className="section-card" variant="borderless">
            <Radio.Group
              value={selectedPayment}
              onChange={(e) => setSelectedPayment(e.target.value)}
              className="payment-group"
            >
              <Space direction="vertical" className="payment-list" size={0}>
                {paymentMethods.map(method => (
                  <Radio key={method.id} value={method.id} className="payment-radio">
                    <Card
                      hoverable
                      className={`payment-card ${selectedPayment === method.id ? 'selected' : ''}`}
                    >
                      <Space align="center" size={0} className="payment-content">
                        <Flex className={`payment-icon payment-icon-${method.id}`}>{method.icon}</Flex>
                        <Space direction="vertical" className="payment-info" size={0}>
                          <Title level={4} className="payment-name">{method.name}</Title>
                          <Text type="secondary">{method.description}</Text>
                        </Space>
                      </Space>
                    </Card>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </Card>

          {/* Submit Button */}
          <Flex justify="center" className="submit-section">
            <Button
              type="primary"
              size="large"
              onClick={handleDeposit}
              disabled={!selectedServer || !selectedCharacter || !selectedPackage || !selectedPayment}
              className="submit-button"
            >
              {t('deposit.proceedPayment')}
            </Button>
          </Flex>
      </Content>
    </Layout>
  );
};

export default DepositPage;

